import React, { useEffect, useState, useRef } from 'react';
import axiosInstance from '../../axiosInstance';
import { Sun, Moon, User, LogOut, ChevronDown, Clock } from 'lucide-react';

function GreetingDashboard() {
  const [user, setUser] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [timeOfDay, setTimeOfDay] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    const updateGreeting = () => {
      const hour = currentTime.getHours();
      if (hour >= 5 && hour < 12) setTimeOfDay('morning');
      else if (hour >= 12 && hour < 14) setTimeOfDay('noon');
      else if (hour >= 14 && hour < 17) setTimeOfDay('afternoon');
      else if (hour >= 17 && hour < 20) setTimeOfDay('evening');
      else setTimeOfDay('night');
    };

    updateGreeting();

    const fetchCompanyDetails = async () => {
      try {
        setIsLoading(true);
        const response = await axiosInstance.get("/company/getCompanyDetail");
        setUser(response.data.data);
      } catch (error) {
        console.error('Error fetching company details', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompanyDetails();

    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      clearInterval(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [currentTime]);

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/login";
  };

  const getInitials = (name = '') => {
    const words = name.trim().split(' ');
    return words.length >= 2
      ? `${words[0][0]}${words[1][0]}`.toUpperCase()
      : name.slice(0, 2).toUpperCase();
  };

  const greetingMessage = () => {
    if (isLoading) return 'Loading...';
    return `Good ${timeOfDay}, ${user.company_name?.slice(0, 3) || 'N/A'}`;
  };

  const getTimeIcon = () => {
    return timeOfDay === 'night' || timeOfDay === 'evening' ? (
      <Moon className="w-3 h-3 text-gray-700" />
    ) : (
      <Sun className="w-3 h-3 text-yellow-500" />
    );
  };

  const getTimeColor = () => {
    switch (timeOfDay) {
      case 'morning': return 'bg-amber-400';
      case 'noon': return 'bg-yellow-400';
      case 'afternoon': return 'bg-orange-400';
      case 'evening': return 'bg-purple-400';
      case 'night': return 'bg-indigo-400';
      default: return 'bg-emerald-400';
    }
  };

  return (
    <div className="relative w-full h-24 flex items-center justify-center px-4">
      {/* Centered vertical stack */}
      <div className="relative w-full h-full flex flex-col justify-between items-center">

        {/* Top: Avatar */}
        <div ref={dropdownRef} className="relative z-10">
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-2 group"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <div className={`relative w-12 h-12 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-500/20 backdrop-blur-md shadow-lg flex items-center justify-center text-white font-medium transition-all duration-300 ${isHovering ? 'ring-2 ring-white/30 scale-105' : ''}`}>
              {user.company_name ? (
                <span className="text-lg">{getInitials(user.company_name)}</span>
              ) : (
                <User className="w-5 h-5" />
              )}
              <div className={`absolute -bottom-1 -right-1 p-1 bg-white rounded-full shadow-sm transition-transform duration-300 ${isHovering ? 'scale-110 rotate-6' : ''}`}>
                {getTimeIcon()}
              </div>
            </div>
            <ChevronDown className={`w-4 h-4 text-gray-300 transition-all duration-300 ${dropdownOpen ? 'rotate-180 text-white' : isHovering ? 'text-white translate-y-0.5' : ''}`} />
          </button>

          {dropdownOpen && (
            <div className="absolute left-[-72px] mt-2 w-56 bg-white/5 backdrop-blur-xl rounded-xl shadow-2xl border border-white/10 overflow-hidden z-50 animate-dropdownSlide">
              <div className="px-4 py-3 border-b border-white/10">
                <p className="text-sm font-medium text-white">{user.company_name || 'Your Account'}</p>
                <div className="flex items-center mt-1 space-x-2">
                  <Clock className="w-3 h-3 text-white/60" />
                  <p className="text-xs text-white/60">
                    {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} • {timeOfDay.charAt(0).toUpperCase() + timeOfDay.slice(1)}
                  </p>
                </div>
              </div>
              <div className="p-1">
                <button
                  onClick={handleLogout}
                  className="w-full px-4 py-2.5 text-left text-rose-100/90 hover:bg-rose-500/10 rounded-lg flex items-center gap-2 transition-all duration-200 hover:pl-5"
                  onMouseEnter={() => setIsHovering('logout')}
                  onMouseLeave={() => setIsHovering(false)}
                >
                  <LogOut className={`w-4 h-4 transition-transform ${isHovering === 'logout' ? 'rotate-12' : ''}`} />
                  <span>Sign out</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Bottom: Greeting */}
        <div className={`group px-4 text-center transition-all duration-300`}>
          <p className="text-xs text-white/70 mb-0.5 group-hover:text-white/90">Welcome back</p>
          <h2 className="text-sm sm:text-lg font-semibold text-white">
            {greetingMessage()}
            <span className={`inline-block ml-2 h-2 w-2 rounded-full ${getTimeColor()} transition-all duration-500 ${isHovering === 'greeting' ? 'opacity-100 scale-125' : 'opacity-70'}`}></span>
          </h2>
        </div>
      </div>

      <style>{`
        @keyframes dropdownSlide {
          0% { opacity: 0; transform: translateY(-8px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-dropdownSlide {
          animation: dropdownSlide 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>
    </div>
  );
}

export default GreetingDashboard;

// import React, { useEffect, useState, useRef } from 'react';
// import axiosInstance from '../../axiosInstance';
// import { Sun, Moon, User, LogOut, ChevronDown, Settings } from 'lucide-react';

// function GreetingDashboard() {
//   const [user, setUser] = useState({});
//   const [isLoading, setIsLoading] = useState(true);
//   const [timeOfDay, setTimeOfDay] = useState('');
//   const [dropdownOpen, setDropdownOpen] = useState(false);
//   const [isHovering, setIsHovering] = useState(false);
//   const dropdownRef = useRef(null);

//   useEffect(() => {
//     const hour = new Date().getHours();
//     setTimeOfDay(hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening');

//     const fetchCompanyDetails = async () => {
//       try {
//         setIsLoading(true);
//         const response = await axiosInstance.get("/company/getCompanyDetail");
//         setUser(response.data.data);
//       } catch (error) {
//         console.error('Error fetching company details', error);
//       } finally {
//         setIsLoading(false);
//       }
//     };

//     fetchCompanyDetails();

//     const handleClickOutside = (e) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
//         setDropdownOpen(false);
//       }
//     };
//     document.addEventListener('mousedown', handleClickOutside);
//     return () => document.removeEventListener('mousedown', handleClickOutside);
//   }, []);

//   const handleLogout = () => {
//     localStorage.clear();
//     window.location.href = "/login";
//   };

//   const getInitials = (name = '') => {
//     const words = name.trim().split(' ');
//     return words.length >= 2
//       ? `${words[0][0]}${words[1][0]}`.toUpperCase()
//       : name.slice(0, 2).toUpperCase();
//   };

//   const greetingMessage = () => {
//     if (isLoading) return 'Loading...';
//     return `Good ${timeOfDay}, ${user.company_name?.slice(0, 3) || 'N/A'}`;
//   };

//   return (
//     <div className="relative w-full flex items-center justify-between py-2 px-4">

//       {/* User Dropdown - Enhanced with more interactivity */}
//       <div className="relative" ref={dropdownRef}>
//         <button
//           onClick={() => setDropdownOpen(!dropdownOpen)}
//           className="flex items-center gap-2 group"
//           onMouseEnter={() => setIsHovering(true)}
//           onMouseLeave={() => setIsHovering(false)}
//         >
//           <div className={`relative w-10 h-10 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-500/20 backdrop-blur-md shadow-lg flex items-center justify-center text-white font-medium transition-all duration-300 ${isHovering ? 'ring-2 ring-white/30 scale-105' : ''}`}>
//             {user.company_name ? (
//               <span className="text-lg">{getInitials(user.company_name)}</span>
//             ) : (
//               <User className="w-5 h-5" />
//             )}
//             <div className={`absolute -bottom-1 -right-1 p-1 bg-white rounded-full shadow-sm transition-transform duration-300 ${isHovering ? 'scale-110 rotate-6' : ''}`}>
//               {timeOfDay === 'evening' ? (
//                 <Moon className="w-3 h-3 text-gray-700" />
//               ) : (
//                 <Sun className="w-3 h-3 text-yellow-500" />
//               )}
//             </div>
//           </div>
//           <ChevronDown className={`w-4 h-4 text-gray-300 transition-all duration-300 ${dropdownOpen ? 'rotate-180 text-white' : isHovering ? 'text-white translate-y-0.5' : ''}`} />
//         </button>

//         {/* Enhanced Dropdown Menu */}
//         {dropdownOpen && (
//           <div className="absolute left-0 mt-2 w-56 bg-white/5 backdrop-blur-xl rounded-xl shadow-2xl border border-white/10 overflow-hidden z-50 animate-dropdownSlide">
//             <div className="px-4 py-3 border-b border-white/10">
//               <p className="text-sm font-medium text-white">{user.company_name || 'Your Account'}</p>
//               {/* <p className="text-xs text-white/60 mt-1">Premium Member</p> */}
//             </div>
//             <div className="p-1">
           
//               <button
//                 onClick={handleLogout}
//                 className="w-full px-4 py-2.5 text-left text-rose-100/90 hover:bg-rose-500/10 rounded-lg flex items-center gap-2 transition-all duration-200 hover:pl-5"
//                 onMouseEnter={() => setIsHovering('logout')}
//                 onMouseLeave={() => setIsHovering(false)}
//               >
//                 <LogOut className={`w-4 h-4 transition-transform ${isHovering === 'logout' ? 'rotate-12' : ''}`} />
//                 <span>Sign out</span>
//               </button>
//             </div>
//           </div>
//         )}
//       </div>

//       {/* Interactive Greeting Card */}
//       <div 
//         className={`group p-3 pr-5 rounded-xl bg-gradient-to-r from-white/5 to-white/2 backdrop-blur-sm shadow-lg transition-all duration-500 ${isHovering ? 'from-white/8 to-white/4' : ''}`}
//         onMouseEnter={() => setIsHovering('greeting')}
//         onMouseLeave={() => setIsHovering(false)}
//       >
//         <p className="text-xs text-white/70 mb-0.5 transition-all duration-300 group-hover:text-white/90">Welcome back</p>
//         <h2 className="text-sm sm:text-lg font-semibold text-white whitespace-nowrap">
//           {greetingMessage()}
//           <span className={`inline-block ml-2 h-2 w-2 rounded-full bg-emerald-400 transition-all duration-500 ${isHovering === 'greeting' ? 'opacity-100 scale-125' : 'opacity-70'}`}></span>
//         </h2>
//         <div className={`absolute bottom-0 left-0 h-px w-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-opacity duration-500 ${isHovering === 'greeting' ? 'opacity-100' : 'opacity-0'}`}></div>
//       </div>

//       {/* Enhanced Animations */}
//       <style>{`
//         @keyframes dropdownSlide {
//           0% { opacity: 0; transform: translateY(-8px) scale(0.98); }
//           100% { opacity: 1; transform: translateY(0) scale(1); }
//         }
//         .animate-dropdownSlide {
//           animation: dropdownSlide 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
//         }
//       `}</style>
//     </div>
//   );
// }

// export default GreetingDashboard; 


// import React, { useEffect, useState } from 'react';
// import axiosInstance from '../../axiosInstance';
// import { Sun, Moon, User } from 'lucide-react';

// function GreetingDashboard() {
//   const [user, setUser] = useState({});
//   const [isLoading, setIsLoading] = useState(true);
//   const [timeOfDay, setTimeOfDay] = useState('');
//   const [isHovered, setIsHovered] = useState(false);

//   useEffect(() => {
//     const hour = new Date().getHours();
//     setTimeOfDay(
//       hour < 12 ? 'morning' :
//         hour < 18 ? 'afternoon' :
//           'evening'
//     );

//     const fetchCompanyDetails = async () => {
//       try {
//         setIsLoading(true);
//         const response = await axiosInstance.get("/company/getCompanyDetail");
//         setUser(response.data.data);
//       } catch (error) {
//         console.log('Error fetching company details', error);
//       } finally {
//         setIsLoading(false);
//       }
//     };

//     fetchCompanyDetails();
//   }, []);

//   const getShortName = (name) => {
//     if (!name) return 'N/A';
//     if (name.length <= 5) return name;
//     return name.substring(0, 3);
//   };

//   const greetingMessage = () => {
//     if (isLoading) return 'Loading...';
//     return `Good ${timeOfDay}, ${getShortName(user.company_name)}`;
//   };

//   return (
//     <div className="relative w-full flex  items-center py-2 overflow-hidden">
//       {/* Greeting Card */}
//       <div
//         className="group relative z-10 p-4 sm:p-5 rounded-2xl backdrop-blur-lg border-white/20 shadow-lg transition-transform duration-300 hover:scale-[1.01] cursor-pointer"
//         onMouseEnter={() => setIsHovered(true)}
//         onMouseLeave={() => setIsHovered(false)}
//         style={{
//           animation: 'fadeSlideIn 0.6s ease-out forwards',
//           opacity: 0,
//           transform: 'translateY(10px)'
//         }}
//       >
//         <div className="flex items-center gap-3">
//           {/* Profile Icon */}
//           <div className="relative">
//             <div className="w-10 h-10 flex items-center justify-center rounded-full shadow-inner ring-2 ring-gradient animate-glow">
//               {user.company_name ? (
//                 <span className="text-lg font-medium text-white">
//                   {user.company_name.charAt(0).toUpperCase()}
//                 </span>
//               ) : (
//                 <User className="w-5 h-5 text-white" />
//               )}
//             </div>

//             {/* Sun or Moon icon */}
//             <div className={`
//               absolute -bottom-1 -right-1 p-1 rounded-full 
//               bg-white/90 shadow-sm transition-transform duration-200 
//               ${isHovered ? 'scale-110 rotate-6' : 'scale-100 rotate-0'}
//             `}>
//               {timeOfDay === 'evening' ? (
//                 <Moon className="w-3 h-3 text-gray-800" />
//               ) : (
//                 <Sun className="w-3 h-3 text-yellow-600" />
//               )}
//             </div>
//           </div>

//           {/* Textual Greeting */}
//           <div>
//             <p className="text-xs text-white/70">Welcome back</p>
//             <h2 className="text-lg sm:text-xl font-semibold text-white whitespace-nowrap">
//               {greetingMessage()}
//             </h2>
//           </div>
//         </div>
//       </div>

//       {/* Custom Animations */}
//       <style>{`
//         @keyframes fadeSlideIn {
//           0% { opacity: 0; transform: translateY(10px); }
//           100% { opacity: 1; transform: translateY(0); }
//         }

//         @keyframes pulseBlob {
//           0%, 100% { transform: scale(1); opacity: 0.2; }
//           50% { transform: scale(1.05); opacity: 0.3; }
//         }

//         .animate-pulseBlob {
//           animation: pulseBlob 6s infinite ease-in-out;
//         }

//         .animate-glow {
//           box-shadow: 0 0 0 rgba(255, 255, 255, 0);
//           transition: box-shadow 0.4s ease-in-out;
//         }

//         .animate-glow {
//           box-shadow: 0 0 10px 2px rgba(255, 255, 255, 0.2);
//         }
//       `}</style>
//     </div>
//   );
// }

// export default GreetingDashboard;

// import React from 'react'

// function GreetingDashboard() {
//   return (
//     <div className="bg-gradient-to-r from-purple-600 to-blue-500 text-white px-4 py-2 rounded-lg inline-block">
//         Welcome, Atom Aviation
//       </div>
//   )
// }

// export default GreetingDashboard



// import React, { useEffect } from 'react'
// import axiosInstance from '../../axiosInstance'


// function GreetingDashboard() {
//   const [user, setUser] = React.useState({})
//   useEffect(()=>{
//     const fetchCompanyDetails = async () =>{
//       try {
//         const response = await axiosInstance.get("/company/getCompanyDetail");
//         // console.log('here are the details******', response.data.data);
//         setUser(response.data.data)
//       }catch(error){
//         console.log('error fetching company details', error);
//       }
//     };
//     fetchCompanyDetails();
//   },[])
//   return (
//     <div className="flex items-center py-2">
//       <div className="bg-gradient-to-r from-purple-600 to-blue-500 text-white px-4 py-2 rounded-lg inline-block">
//         Welcome, {user.company_name || 'N/A'}
//       </div>
//     </div>
//   )
// }

// export default GreetingDashboard
